import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  PagedList,
  PerksReimbursementHistoryResponse,
  ReimbursementHistoryFilter,
  getPerksReimbursementHistory,
} from 'core'

import { Col, RangePickerV2, Row, Search, Table } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'

type PerksReimbursementHistoryProps = {
  planPeriodId: string
}

const pagedColumnDefaultValues = {
  items: [],
  searchValue: '',
  pageCount: 1,
  pageNumber: 1,
  pageSize: 10,
  totalItemCount: 0,
}
const PerksReimbursementHistory: React.FC<PerksReimbursementHistoryProps> = (props) => {
  const { t: translate } = useTranslation()
  const [filter, setFilter] = useState<ReimbursementHistoryFilter>({
    pageNumber: 1,
    pageSize: 25,
    orderColumn: 'scheduleDate',
    orderDescending: true,
  })
  const {
    data: pagedReimbursementHistory,
    isLoading: isReimbursementHistoryLoading,
    sendQuery: sendGetReimbursementHistoryQuery,
  } = useQueryRequestV2<PagedList<PerksReimbursementHistoryResponse>>({ initialState: pagedColumnDefaultValues })

  const [dateRangeValues, setDateRangeValues] = useState<{ fromDate: string; toDate: string }>({
    fromDate: null,
    toDate: null,
  })

  useEffect(() => {
    ;(async () => {
      await sendGetReimbursementHistoryQuery(() =>
        getPerksReimbursementHistory({
          ...filter,
          planPeriodId: props.planPeriodId,
          fromDate: dateRangeValues.fromDate,
          toDate: dateRangeValues.toDate,
        }),
      )
    })()
  }, [filter, props.planPeriodId])

  useEffect(() => {
    setFilter((filter: ReimbursementHistoryFilter) => ({
      ...filter,
      fromDate: dateRangeValues.fromDate,
      toDate: dateRangeValues.toDate,
    }))

    setDateRangeValues({
      fromDate: dateRangeValues.fromDate,
      toDate: dateRangeValues.toDate,
    })
  }, [props.planPeriodId])
  const handleDateChange = async (values: any) => {
    const fromDateValue = values ? values[0] : null
    const toDateValue = values ? values[1] : null
    setDateRangeValues({
      fromDate: fromDateValue,
      toDate: toDateValue,
    })
    await sendGetReimbursementHistoryQuery(() => {
      return getPerksReimbursementHistory({
        ...filter,
        planPeriodId: props.planPeriodId,
        fromDate: fromDateValue,
        toDate: toDateValue,
        pageNumber: 1,
      })
    })

    setFilter({
      ...filter,
      fromDate: fromDateValue,
      toDate: toDateValue,
      pageNumber: 1,
    })
  }

  const onSearch = (searchValue: string) => {
    setFilter({
      ...filter,
      searchValue: searchValue,
      pageNumber: 1,
    })
  }

  const onTableFilterChanged = (pagination: any, filters: any, sorter: any) => {
    setFilter({
      ...filter,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
      orderColumn: sorter.field,
      orderDescending: sorter.order !== 'ascend',
    })
  }

  const columns: any[] = [
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-history.first-name'),
      dataIndex: 'firstName',
      key: 'firstname',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-history.last-name'),
      dataIndex: 'lastName',
      key: 'lastname',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-history.date-scheduled'),
      dataIndex: 'scheduleDate',
      key: 'dateScheduled',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (scheduleDate: string, record: any) => parseZoneFormat(scheduleDate),
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-history.category'),
      dataIndex: 'category',
      key: 'category',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-history.service-provider'),
      dataIndex: 'serviceProvider',
      key: 'serviceProvider',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: translate('admin-dashboard.reimbursements.perks-reimbursements-history.expense-amount'),
      dataIndex: 'expenseAmount',
      key: 'expenseAmount',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (expenseAmount: string, record: any) => '$' + expenseAmount,
    },
  ]
  const columnToSort: any = columns.find((c) => c.key === filter.orderColumn)
  if (columnToSort) {
    columnToSort.defaultSortOrder = filter.orderDescending ? 'descend' : 'ascend'
  }
  return (
    <>
      <Row align={'middle'} justify={'space-between'}>
        <Col span={18}>
          <Row align={'middle'} gutter={10}>
            <Col xs={24} sm={24} md={8} lg={8} xl={8} className="mb-8">
              <Search onSearch={onSearch} allowClear enterButton />
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={16} className="mb-8">
              <Row align="middle" gutter={6} justify={'center'}>
                <Col>
                  <label>Date Scheduled</label>
                </Col>
                <Col>
                  <RangePickerV2 allowClear className={'w-100'} format="MM/DD/YYYY" onChange={handleDateChange} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
      <Table
        dataSource={pagedReimbursementHistory?.items}
        loading={isReimbursementHistoryLoading}
        onChange={onTableFilterChanged}
        columns={columns}
        scroll={{ x: 1000 }}
        pagination={{
          total: pagedReimbursementHistory?.totalItemCount,
          pageSize: pagedReimbursementHistory?.pageSize,
          current: pagedReimbursementHistory?.pageNumber,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '30', '40'],
        }}
      />
    </>
  )
}

export default PerksReimbursementHistory
